import _get from 'lodash/get'
import _isEqual from 'lodash/isEqual'
import { memo, useMemo } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { P, isMatching } from 'ts-pattern'
import { z } from 'zod'

import Card from '@nxt-insurance/ui-kit/components/cards/Card'
import UiKitRadioCardSet from '@nxt-insurance/ui-kit/components/formComponents/RadioCardSet'
import Separator from '@nxt-insurance/ui-kit/components/layoutComponents/Separator'
import Stack from '@nxt-insurance/ui-kit/components/layoutComponents/Stack'
import { caption, h2, h5 } from '@nxt-insurance/ui-kit/core/typography'

import type { HorizontalAlignment } from '@/cms/helperTypes'
import { colorEnum, horizontalAlignmentEnum, iconSchema } from '@/cms/helperTypes'
import { Icon } from '@/components/Icon'
import { BlockContent } from '@/components/renderers/BlockContent'
import { EVENTS } from '@/constants/analytics'
import { useAnalytics } from '@/context/AnalyticsContext'
import type { PortableTextTypeComponentProps } from '@portabletext/react'

const radioCardSet = z.union([
  z.object({
    _key: z.string(),
    _type: z.literal('radioCardSet'),
    borderColor: colorEnum.optional(),
    items: z.array(
      z.object({
        _key: z.string(),
        title: z.string(),
        subtitle: z.string().optional(),
        content: z.array(z.unknown()),
        contentTitle: z.string().optional(),
        icon: iconSchema,
        description: z.string().optional(),
        align: horizontalAlignmentEnum.optional(),
      })
    ),
  }),
  z.object({
    _key: z.string(),
    _type: z.literal('radioCardSet'),
    layout: z.union([z.literal('wide'), z.literal('compact')]),
    name: z.string(),
    options: z.array(
      z.object({
        value: z.string(),
        icon: iconSchema,
        title: z.string(),
        subtitle: z.string().optional(),
        description: z.string().optional(),
        topTag: z
          .object({
            label: z.string(),
          })
          .optional(),
      })
    ),
  }),
])

const valueMatcher = isMatching({
  options: P.not(P.nullish),
})

const optionMatcher = isMatching({
  _key: P.string,
})

export const RadioCardSet = memo(
  (props: {
    node: PortableTextTypeComponentProps<unknown>
    width: number
    align: HorizontalAlignment
    sectionName: string
  }) => {
    const { control } = useFormContext()
    const value = useMemo(() => radioCardSet.parse(props.node.value), [props.node.value])
    const { field, fieldState } = useController({
      control,
      name: valueMatcher(value) ? value.name : value._key,
      defaultValue: valueMatcher(value) ? undefined : value.items[0]._key,
    })

    const radioOptions = useMemo(() => {
      if (valueMatcher(value)) {
        return value.options.map((item) => {
          return {
            ...item,
            icon: <Icon icon={item.icon} size="medium" />,
          }
        })
      }
      return value.items.map((item) => {
        return {
          icon: <Icon icon={item.icon} size="medium" />,
          title: item.title,
          value: item._key,
        }
      })
    }, [value])

    const selectedElement = useMemo(() => {
      if (valueMatcher(value)) {
        return value.options.find((item) => item.value === field.value)
      }
      return value.items.find((item) => item._key === field.value)
    }, [value, field.value])

    const { track } = useAnalytics()

    return (
      <div className="flex w-full max-w-[600px] flex-col">
        <Stack>
          <UiKitRadioCardSet
            layout={valueMatcher(value) ? value.layout : 'compact'}
            options={radioOptions}
            value={field.value}
            errorMessage={fieldState.error?.message}
            onValueChange={(newValue) => {
              if (radioOptions.length === 0) {
                return
              }

              if (radioOptions.length > 1) {
                field.onChange(newValue)
              }

              const analyticsData = {
                sectionName: props.sectionName,
                value: newValue,
              }
              track(EVENTS.RadioCardSetClicked, analyticsData)
            }}
          />
          {selectedElement && optionMatcher(selectedElement) ? (
            <Card borderColor="brandDark">
              <Stack space="xxsmall">
                {selectedElement.contentTitle ? (
                  <p className={h2({ align: 'center' })}>{selectedElement.contentTitle}</p>
                ) : null}
                {selectedElement.subtitle ? (
                  <p className={h5({ align: 'center' })}>{selectedElement.subtitle}</p>
                ) : null}
                {selectedElement.description ? (
                  <p className={caption({ align: 'center', emphasis: 'medium' })}>
                    {selectedElement.description}
                  </p>
                ) : null}
              </Stack>
              {selectedElement.content ? (
                <div className="flex flex-col">
                  {selectedElement.contentTitle ||
                  selectedElement.subtitle ||
                  selectedElement.description ? (
                    <Separator lineType="solid" />
                  ) : null}
                  <BlockContent
                    blocks={selectedElement.content}
                    align={selectedElement.align ?? undefined}
                    sectionName={props.sectionName}
                    width={props.width}
                  />
                </div>
              ) : null}
            </Card>
          ) : null}
        </Stack>
      </div>
    )
  },
  _isEqual
)
